// CreateControlDialog — modal form for creating a new control.
// Creates an ownership role for the current user, then creates the control.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { controlsApi, type CreateControlRequest } from '@/lib/api/controls'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'

interface CreateControlDialogProps {
  categoryId: string
  categoryName: string
  open: boolean
  onClose: () => void
  onCreated: (controlId: string) => void
}

export function CreateControlDialog({
  categoryId,
  categoryName,
  open,
  onClose,
  onCreated,
}: CreateControlDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const ctrlApi = controlsApi(api)
  const ownApi = ownershipRolesApi(api)

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [controlActivity, setControlActivity] = useState('')
  const [controlRationale, setControlRationale] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Control name is required')

      // Step 1: Create ownership role
      const ownerRole = await ownApi.create({
        label: `Owner: ${name.trim()}`,
      })

      // Step 2: Create the control
      const data: CreateControlRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        category_id: categoryId,
        owner_role_id: ownerRole.id,
        control_activity: controlActivity.trim() || undefined,
        control_rationale: controlRationale.trim() || undefined,
      }

      return ctrlApi.create(data)
    },
    onSuccess: (ctrl) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'control') })
      resetForm()
      onCreated(ctrl.id)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function resetForm() {
    setName('')
    setDescription('')
    setControlActivity('')
    setControlRationale('')
    setError(null)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Create Control</h2>
            <p className="text-sm text-muted-foreground">in {categoryName}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ctrl-name">Control Name *</Label>
            <Input
              id="ctrl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Quarterly access review"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ctrl-desc">Description</Label>
            <textarea
              id="ctrl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the control"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ctrl-activity">What the control does</Label>
            <textarea
              id="ctrl-activity"
              value={controlActivity}
              onChange={(e) => setControlActivity(e.target.value)}
              placeholder="Describe the control activity"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ctrl-rationale">Why the control exists</Label>
            <textarea
              id="ctrl-rationale"
              value={controlRationale}
              onChange={(e) => setControlRationale(e.target.value)}
              placeholder="Business rationale for this control"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !name.trim()}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Control'}
          </Button>
        </div>
      </div>
    </div>
  )
}
